import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link, useLocation } from 'react-router-dom';
import SettingsIcon from '@mui/icons-material/Settings';
import NotificationsIcon from '@mui/icons-material/NotificationsNone';
import UnreadBadge, { unreadLabel } from '../notifications/UnreadBadge';
import { useUnreadCount } from '../../hooks/useUnreadCount';
import { useIikoCloudStatus } from '../../hooks/useIikoCloudStatus';
import { isTelegramMiniApp } from '../../utils/platform';
import {
  MOTION,
  PAGE_BACKGROUND_SX,
  SECTION_ACCENTS,
  motionSx,
  navGradientLineSx,
  navSurfaceSx,
} from '../../styles/tokens';
import { useSlidingIndicator } from '../motion/useSlidingIndicator';
import FadeIn from '../motion/FadeIn';

// Разделы-табы десктопной шапки; «Настройки» — отдельная иконка справа.
// «Кухня» — только у серверов с подключением iikoCloud, после «Событий»
const NAV_TABS = [
  { to: '/metrics', label: 'Выручка' },
  { to: '/cost', label: 'Себестоимость' },
  { to: '/dashboard', label: 'События' },
];
const KITCHEN_TAB = { to: '/kitchen', label: 'Кухня' };
const SETTINGS_PATH = '/profile';
export const NOTIFICATIONS_PATH = '/notifications';

// Высота закреплённой шапки на десктопе — под ней прилипают закреплённые заголовки таблиц (ABC-анализ)
export const DESKTOP_HEADER_HEIGHT = 64;
// Подчёркивание рисуется этой ширины и растягивается scaleX до ширины таба (анимируем только transform)
const UNDERLINE_BASE_WIDTH = 100;
const NAV_TEXT_COLOR = '#a1a1aa';

export function PageContainer({ children }) {
  return (
    <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 } }}>
      {children}
    </Box>
  );
}

// Иконки-кнопки справа в шапке (уведомления, настройки)
const headerIconButtonSx = (active) => ({
  width: 40,
  height: 40,
  borderRadius: '10px',
  color: active ? '#ffffff' : NAV_TEXT_COLOR,
  backgroundColor: active ? 'rgba(255,255,255,0.08)' : 'transparent',
  '&:hover': { color: '#ffffff', backgroundColor: 'rgba(255,255,255,0.06)' },
  '&.Mui-focusVisible': { outline: '2px solid #ffffff', outlineOffset: '2px' },
});

function DesktopNav() {
  const location = useLocation();
  const telegram = isTelegramMiniApp();
  const settingsActive = location.pathname === SETTINGS_PATH;
  const notificationsActive = location.pathname === NOTIFICATIONS_PATH;
  const unread = useUnreadCount();
  const iikocloud = useIikoCloudStatus();
  const tabs = iikocloud ? [...NAV_TABS, KITCHEN_TAB] : NAV_TABS;
  const activeTab = tabs.find((tab) => tab.to === location.pathname) || null;
  const underline = useSlidingIndicator(activeTab?.to ?? null);

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 30,
        width: '100%',
        ...navSurfaceSx(telegram),
        // Градиентная линия вместо серой нижней границы; подчёркивание таба ложится поверх неё
        '&::after': { ...navGradientLineSx('bottom'), zIndex: 1 },
      }}
    >
      <PageContainer>
        <Box
          sx={{
            height: DESKTOP_HEADER_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography sx={{ fontWeight: 700, letterSpacing: '0.02em', flexShrink: 0 }}>
            Pro Resto
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minWidth: 0 }}>
            <Box
              ref={underline.containerRef}
              component="nav"
              aria-label="Разделы"
              sx={{ position: 'relative', display: 'flex', alignItems: 'stretch', gap: '28px', height: '100%' }}
            >
              {/* Подчёркивание 2px по нижнему краю шапки, поверх градиентной линии; переезжает между табами */}
              <Box
                aria-hidden="true"
                sx={{
                  position: 'absolute',
                  left: 0,
                  bottom: 0,
                  width: UNDERLINE_BASE_WIDTH,
                  height: '2px',
                  transformOrigin: '0 0',
                  zIndex: 2,
                  pointerEvents: 'none',
                  backgroundColor: underline.position ? SECTION_ACCENTS[underline.position.key].tab : 'transparent',
                  opacity: underline.position && !underline.position.hidden ? 1 : 0,
                  transform: underline.position
                    ? `translateX(${underline.position.x}px) scaleX(${underline.position.width / UNDERLINE_BASE_WIDTH})`
                    : 'none',
                  ...(underline.animate && motionSx(['transform', 'background-color', 'opacity'])),
                }}
              />
              {tabs.map((tab) => {
                const active = location.pathname === tab.to;
                const accent = SECTION_ACCENTS[tab.to].tab;
                return (
                  <Box
                    key={tab.to}
                    ref={underline.itemRef(tab.to)}
                    component={Link}
                    to={tab.to}
                    aria-current={active ? 'page' : undefined}
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      height: '100%',
                      fontSize: '15px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      textDecoration: 'none',
                      color: active ? accent : NAV_TEXT_COLOR,
                      '&:hover': { color: active ? accent : '#ffffff' },
                      '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: '2px', borderRadius: '4px' },
                      ...motionSx(['color'], MOTION.fast),
                    }}
                  >
                    {tab.label}
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2 }}>
              <IconButton
                component={Link}
                to={NOTIFICATIONS_PATH}
                aria-label={unreadLabel(unread)}
                aria-current={notificationsActive ? 'page' : undefined}
                sx={headerIconButtonSx(notificationsActive)}
              >
                <UnreadBadge count={unread}>
                  <NotificationsIcon fontSize="small" />
                </UnreadBadge>
              </IconButton>
              <IconButton
                component={Link}
                to={SETTINGS_PATH}
                aria-label="Настройки"
                aria-current={settingsActive ? 'page' : undefined}
                sx={headerIconButtonSx(settingsActive)}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </PageContainer>
    </Box>
  );
}

export const TELEGRAM_TOP_INSET =
  'max(8px, calc(var(--tg-safe-area-inset-top, 0px) + var(--tg-content-safe-area-inset-top, 0px)))';

export const MOBILE_TOP_INSET =
  `max(16px, env(safe-area-inset-top, 0px), ${TELEGRAM_TOP_INSET})`;

// Высота нижней панели навигации на телефоне без safe-area (см. MobileNav в AppContent)
export const MOBILE_NAV_HEIGHT = 64;

export default function AppLayout({ children }) {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const telegram = isTelegramMiniApp();
  const location = useLocation();

  const paddingTop = desktop ? '24px' : MOBILE_TOP_INSET;

  // Контент не прячется под нижней панелью: её высота + safe-area + отступ
  const paddingBottom = desktop
    ? '24px'
    : `calc(${MOBILE_NAV_HEIGHT}px + 16px + env(safe-area-inset-bottom, 0px))`;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        // В Telegram — прежний фон, без свечения и сетки
        ...(telegram
          ? { background: 'linear-gradient(180deg, #0d0d0d 0%, #1a1a1a 100%)' }
          : PAGE_BACKGROUND_SX),
      }}
    >
      {desktop && <DesktopNav />}
      <Box
        sx={{
          flexGrow: 1,
          pt: paddingTop,
          pb: paddingBottom,
        }}
      >
        <PageContainer>
          {/* Смена страницы — единственный fade, MOTION.fast; key — новый раздел появляется заново */}
          <FadeIn key={location.pathname} timeout={MOTION.fast}>
            <Box>{children}</Box>
          </FadeIn>
        </PageContainer>
      </Box>
    </Box>
  );
}

export function useDesktopLayout() {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.up('md'));
}
